import { inject } from "@angular/core";
import { CanActivateFn } from "@angular/router";
import { CertificateState } from "../../certificate.state";
import { CertificateService } from "../../certificate.service";

/**
 * Initializes/re-initializes the CertificateState when entering the parent route
 * /certificates/:type/:id. It will only reset if the type/id changed. Navigating
 * between child routes (overview <-> section) will NOT re-run this guard since the
 * parent remains active, preserving in-progress form edits.
 */
export const initCertificateStateGuard: CanActivateFn = (route) => {
  const state = inject(CertificateState);
  const certs = inject(CertificateService);
  const type = (route.params["type"] ?? route.firstChild?.params["type"]) as string;
  const id = route.params['id'] ?? route.firstChild?.params["id"];
  const vParam = route.queryParams?.["v"] as string | undefined;
  const versionFromQuery = vParam ? Number(vParam) : undefined;

  // initialise state identity (no-op if same)
  state.initialize(type, id);

  // Load definition if not already loaded (non-blocking)
  if (!state.definition()) {
    if (id === "new") {
      void certs.getCertificateDefinition(type).then(def => {
        if (def) {
          state.definition.set(def);
          // For new certificates, mark all sections as loaded since there's no data to fetch
          const loaded = def.sections.reduce((acc, s) => ({ ...acc, [s.id]: true }), {});
          state.loadedSections.set(loaded);
        }
      });
    } else {
      // When opening an existing certificate, prefer the version passed from list metadata
      void certs.getCertificateDefinition(type, versionFromQuery).then(def => {
        if (def) {
          state.definition.set(def);
          if (typeof versionFromQuery === "number") {
            state.certificateDefinitionVersion.set(versionFromQuery);
          }

          // For existing certificates, prefetch all section data in parallel (only once per certificate)
          for (const section of def.sections) {
            if (!state.loadedSections()[section.id]) {
              void state.loadSection(section.id);
            }
          }
        }
      });
    }
  } else {
    // Definition already loaded, but still check for existing certificate section loading
    if (id !== "new" && state.definition()) {
      const def = state.definition()!;
      for (const section of def.sections) {
        if (!state.loadedSections()[section.id]) {
          void state.loadSection(section.id);
        }
      }
    }
  }

  // No company form prefill — company info is injected directly into the PDF

  return true;
};
